import React, { useCallback, useEffect, useMemo, } from 'react';
import { useTranslation, } from 'react-i18next';
import { useNavigate, } from 'react-router-dom';
import { AtlasForm, IForm, IFormOptions, } from 'atlas-form';
import {
  JourneyVariableEditForm,
  JourneyVariableEditInitialValues,
  JourneyVariableComponents,
  JourneyVariableAdditionalParams,
  JourneyVariableFormOptions,
  onJourneyVariableFormSaved,
  IJourneyVariableFormOptionsProps,
  AlertError,
} from '@Utils';
import { ISharedJourneyVariable, ISharedPatchJourneyVariable, PermissionEnum, CrudEnum, } from 'atlas-shared';
import { fetchJourneyVariable, patchJourneyVariable, getJourneyVariableUpdateSchema, } from '@Api';
import { RuleProtected, } from '@Containers';
import { withEdit, IWithEditProps, } from '@Hocs/with.edit';
import { useAbilities, useAuth, useOrganizations, } from '@Hooks';
import { AppSpinner, } from '@Components';
import { useAppDispatch, } from '@Store';

interface IProps extends IWithEditProps<ISharedJourneyVariable> {}

const JourneyVariableFormEdit = ({ id, value, setValue, formSchema, schemaLoading, saving, save, title, onSaved, onCancel, onChange, hasAdvanced = false, }: IProps) => {
  const { t, } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const abilities = useAbilities();
  const dispatch = useAppDispatch();
  const organizations = useOrganizations();

  useEffect(() => {
    fetchJourneyVariable(id).then(journey_variable => setValue(journey_variable));
  }, [id, setValue, ]);

  const param_props: IJourneyVariableFormOptionsProps = {
    t,
    is_new: false,
    abilities,
    organizations: organizations.organizations,
  };
  const formLayout: IForm | undefined = useMemo(() => value && JourneyVariableEditForm(value, param_props, t), [value, organizations, t, abilities, ]);
  const options: IFormOptions | undefined = useMemo(() => value && JourneyVariableFormOptions(value, param_props, t), [value, organizations, t, abilities, ]);
  
  const onValuesChange = useCallback((changedValue: any, values: ISharedPatchJourneyVariable, form) => {
    onChange?.(values, changedValue, form);
  }, []);
  const onFinish = useCallback((values: ISharedPatchJourneyVariable) => value && save(() => patchJourneyVariable(value.id, values).then(
    res => onSaved ? onSaved(res, navigate) : onJourneyVariableFormSaved(t, 'edit', res, navigate),
    err => {
      AlertError(t, {
        content: err,
      });
    }
  )), [value, save, navigate, t, ]);
  
  if (schemaLoading)
    return <AppSpinner />;

  if (!value)
    return <>{t('ITEM_NOT_FOUND')}</>;

  if (!formSchema)
    return <>{t('SCHEMA_NOT_FOUND')}</>;
  
  if (!organizations.loaded)
    return <>{t('LOADING_DEPENDENCIES')}</>;

  return (
    <RuleProtected
      isAllowed={() => !value || abilities.can(value.organization_id, CrudEnum.Update, PermissionEnum.JourneyVariable, value)}
    >
      <AtlasForm<ISharedPatchJourneyVariable>
        dispatch={dispatch}
        formLayout={formLayout as IForm}
        form={formSchema}
        options={options}
        components={JourneyVariableComponents}
        additionalParams={JourneyVariableAdditionalParams(auth, param_props)}
        onFinish={onFinish}
        onCancel={onCancel}
        onValuesChange={onValuesChange}
        initialValues={JourneyVariableEditInitialValues(value)}
        saving={saving}
        title={title ? t(title) : `${t('EDIT')} ${t('JOURNEY_VARIABLE')}`}
        entityId={value.id}
        entity={value}
        hasAdvanced={hasAdvanced}
      />
    </RuleProtected>
  );
};

export const JourneyVariableFormEditView = React.memo(withEdit(JourneyVariableFormEdit, getJourneyVariableUpdateSchema));
